import React, { useRef, useEffect, useMemo, useState, useCallback } from "react"
import atlas, { AuthenticationType, ControlPosition } from "azure-maps-control"
import "azure-maps-control/dist/atlas.min.css"
import selectedLocation from "./../../../../assets/selectedLocation.png"
import unselectedLocation from "./../../../../assets/unselectedLocation.png"
import mapPlaceholder from "./../../../../assets/karrieKoalaMapPlaceholder.svg"
import { apiService } from "../../../shared/apiService"
import { useCMSReady } from "../../../../domain/CMS/useCMSReady"
import { DataStatus } from "../../../../domain/enums"
import { dataStatusesLoading } from "../../../shared/utils"
import appInsightsLogger from "../../../logging/appInsightsLogger"
import {
  Typography,
  typographyTypes,
} from "../../../shared/components/Typography"
import SkeletonLoader from "../../../shared/components/SkeletonLoader"

export interface Marker {
  id: string
  address: string
  title: string
  lat?: number
  lon?: number
}

interface PracticeMapProps {
  markers: Marker[]
  selectedMarkerId: string | null
  setSelectedMarkerId: React.Dispatch<React.SetStateAction<string | undefined>>
  setGeocodeLocationErrorIds: React.Dispatch<React.SetStateAction<string[]>>
  height?: string
  width?: string
}

const PracticeMap: React.FC<PracticeMapProps> = ({
  markers: initialMarkers,
  selectedMarkerId,
  setSelectedMarkerId,
  setGeocodeLocationErrorIds,
  height = "400px",
  width = "100%",
}) => {
  const { cms: cmsContent } = useCMSReady(["publicProfileSlide"])
  const publicProfileText = cmsContent.publicProfileSlide

  const SUBSCRIPTION_KEY = "subscriptionKey" as AuthenticationType
  const ANONYMOUS = "anonymous" as AuthenticationType
  const NUMBER = "number"
  const CLICK = "click"
  const BOTTOM_RIGHT = "bottom-right" as ControlPosition
  const BOTTOM_LEFT = "bottom-left" as ControlPosition
  const IMPERIAL = "imperial"
  const READY = "ready"

  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<atlas.Map | null>(null)
  const [markers, setMarkers] = useState<Marker[]>([])
  const [markersDataStatus, setMarkersDataStatus] = useState<DataStatus>(
    DataStatus.LOADING,
  )

  const subscriptionKey = window._env_.AZURE_MAPS_SUBSCRIPTION_KEY
  const markersLoading = dataStatusesLoading([markersDataStatus])

  // Updates markers on the map instance
  const updateMarkers = useMemo(() => {
    return (map: atlas.Map) => {
      map.markers.clear()

      const positions = markers
        .filter(
          (marker) =>
            selectedMarkerId === marker.id &&
            !!marker.lat &&
            !!marker.lon &&
            typeof marker.lat === NUMBER &&
            typeof marker.lon === NUMBER,
        )
        .map((marker) => [marker.lon!, marker.lat!] as [number, number])

      if (positions.length > 0) {
        map.setCamera({ center: positions[0], zoom: 15, duration: 1000 })
      }

      markers.forEach((markerData) => {
        if (
          !!markerData.lat &&
          !!markerData.lon &&
          typeof markerData.lat === NUMBER &&
          typeof markerData.lon === NUMBER
        ) {
          const markerPng =
            selectedMarkerId === markerData.id
              ? selectedLocation
              : unselectedLocation
          const marker = new atlas.HtmlMarker({
            position: [markerData.lon, markerData.lat],
            htmlContent: `<img src="${markerPng}" alt="marker" style="width: auto; height: 38px; background: transparent;" />`,
          })

          marker._addEventListener(
            CLICK,
            () => {
              if (markerData.id && selectedMarkerId !== markerData.id) {
                setSelectedMarkerId(markerData.id)
              }
            },
            false,
          )

          map.markers.add(marker)
        }
      })
    }
  }, [markers, selectedMarkerId, setSelectedMarkerId])

  // Initializes the map
  const initMap = useCallback(async () => {
    if (!mapRef.current || !!mapInstanceRef.current) return

    try {
      let mapConfig: { authOptions: atlas.AuthenticationOptions }
      const clientId = window._env_.AZURE_MAPS_CLIENT_ID
      if (clientId) {
        appInsightsLogger.traceInfo(
          "Using managed identity tokens for map rendering",
        )
        mapConfig = {
          authOptions: {
            authType: ANONYMOUS,
            clientId: clientId, // Use Entra ID client ID
            getToken: (
              resolve: (value?: string) => void,
              reject: (reason?: unknown) => void,
            ) => {
              apiService.external.get
                .AZURE_MAPS_ACCESS_TOKEN()
                .then((freshToken) => {
                  if (freshToken.accessToken) {
                    resolve(freshToken.accessToken)
                  } else {
                    appInsightsLogger.traceWarning("No access token received")
                    reject(new Error("No access token available"))
                  }
                })
                .catch((error) => {
                  appInsightsLogger.traceError(
                    "Failed to get Azure Maps token:",
                    error,
                  )
                  reject(error)
                })
            },
          },
        }
      } else {
        // Use subscription key authentication (legacy)
        appInsightsLogger.traceInfo(
          "Initializing map with subscription key authentication",
        )
        if (!subscriptionKey) {
          appInsightsLogger.traceError("No Azure Maps authentication available")
          return
        }
        mapConfig = {
          authOptions: {
            authType: SUBSCRIPTION_KEY,
            subscriptionKey: subscriptionKey,
          },
        }
      }
      const map = new atlas.Map(mapRef.current, mapConfig)
      mapInstanceRef.current = map

      map.events.add(READY, () => {
        appInsightsLogger.traceInfo("Map is ready, adding controls and markers")
        // Add controls once the map is ready
        map.controls.add(new atlas.control.ZoomControl(), {
          position: BOTTOM_RIGHT,
        })

        map.controls.add(new atlas.control.ScaleControl({ unit: IMPERIAL }), {
          position: BOTTOM_LEFT,
        })

        // Update markers once the map is ready
        updateMarkers(map)
      })
    } catch (error) {
      appInsightsLogger.traceError("Failed to initialize Azure Maps", error)
    }
  }, [updateMarkers, subscriptionKey])

  // Adds coordinates to a marker using geocoding
  const addCoords = useCallback(
    async (marker: Marker): Promise<Marker> => {
      let newMarker = marker
      try {
        const geocodedLocation =
          await apiService.external.get.GEOCODED_LOCATION(marker.address, true)
        if (geocodedLocation.score > 0.8) {
          newMarker = {
            ...marker,
            lat: geocodedLocation.position.lat,
            lon: geocodedLocation.position.lon,
          }
        } else {
          setGeocodeLocationErrorIds((prevIds) => [...prevIds, marker.id])
        }
      } catch (error) {
        appInsightsLogger.traceError("Failed to geocode marker address", error)
        setGeocodeLocationErrorIds((prevIds) => [...prevIds, marker.id])
      }
      return newMarker
    },
    [setGeocodeLocationErrorIds],
  )

  // Effect: Geocodes initial markers and sets them to state
  useEffect(() => {
    const geocodeMarkers = async () => {
      if (markersDataStatus === DataStatus.LOADING) {
        setMarkersDataStatus(DataStatus.SENDING)
        const geocodedMarkers = await Promise.all(initialMarkers.map(addCoords))

        // only use successful geocoded markers
        const successfulGeocodedMarkers = geocodedMarkers.filter(
          (marker) => !!marker.lat && !!marker.lon,
        )
        setMarkersDataStatus(DataStatus.LOADED)
        const defaultSelectedMarkerId =
          selectedMarkerId &&
          successfulGeocodedMarkers
            .map((marker) => marker.id)
            .includes(selectedMarkerId)
            ? selectedMarkerId
            : (successfulGeocodedMarkers[0]?.id ?? "")

        if (defaultSelectedMarkerId !== selectedMarkerId) {
          setSelectedMarkerId(defaultSelectedMarkerId)
        }
        setMarkers(successfulGeocodedMarkers)
      }
    }

    geocodeMarkers()
  }, [
    addCoords,
    initialMarkers,
    markersDataStatus,
    selectedMarkerId,
    setSelectedMarkerId,
  ])

  // Effect: Updates markers on the map when markers or selectedMarkerId change
  useEffect(() => {
    if (!!mapInstanceRef.current) {
      updateMarkers(mapInstanceRef.current)
    }
  }, [markers, selectedMarkerId, updateMarkers])

  // Effect: Initializes the map when feature flag is ready and markers are loaded
  useEffect(() => {
    if (!markersLoading) {
      initMap()
    }
  }, [initMap, markersLoading])

  if (markersLoading) {
    return <SkeletonLoader height={height} width={width} />
  }
  return markers.filter((marker) => !!marker.lat && !!marker.lon).length ===
    0 ? (
    <div
      className={`flex-col h-fit py-2 bg-gray-100 flex justify-center items-center`}
      style={{ width }}
    >
      <img
        src={mapPlaceholder}
        alt={publicProfileText?.locationMapAltText}
        className="h-auto w-1/5 object-contain"
      />
      <Typography
        variant={typographyTypes.LABEL}
        text={publicProfileText?.locationMapAltText}
      />
    </div>
  ) : (
    <div ref={mapRef} className={`h-full w-full`} style={{ height, width }} />
  )
}

export default PracticeMap
